import axios from 'smg/axios';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import i18n from '../../i18n';
import { DrawQuoteCard } from '../../quote';

void main();

const pageMax = 5;

// Everything the card draws: the number and date, the pieces with their prices,
// the artwork, who sent the quote, and the order it became.
const quoteListData = 'id,serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteSents,key';

type stateKind = 'empty' | 'error';

interface stateCopy {
    icon: string;
    heading: string;
    body: string;
    action: string;
}

// The page is in exactly one of these when it has nothing to list: the
// customer has never been quoted, or the list did not load.
function drawState($parent: HTMLElement, kind: stateKind, retry?: () => void) {
    const copy: Record<stateKind, stateCopy> = {
        empty: { icon: 'request_quote', heading: i18n('quotesEmptyHeading'), body: i18n('quotesEmptyBody'), action: '#quotes-empty-template' },
        error: { icon: 'cloud_off', heading: i18n('quotesErrorHeading'), body: i18n('quotesErrorBody'), action: '#quotes-error-template' },
    };
    const c = copy[kind];

    $parent.replaceChildren();
    const $state = template.Clone('#quotes-state-template').firstElementChild as HTMLElement;
    ($state.querySelector('.quotes-state-icon') as HTMLElement).textContent = c.icon;
    ($state.querySelector('.quotes-state-heading') as HTMLElement).textContent = c.heading;
    ($state.querySelector('.quotes-state-body') as HTMLElement).textContent = c.body;
    ($state.querySelector('.quotes-state-action') as HTMLElement).appendChild(template.Clone(c.action).firstElementChild as HTMLElement);
    $state.querySelector('.quotes-retry')?.addEventListener('click', () => retry?.());
    $parent.appendChild($state);
}

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const total = axios.get('/v2/quotes', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count);

    const $parent = document.querySelector('#quotes-container') as HTMLElement;
    const $viewMore = template.Clone('#view-more-btn-template').firstElementChild as HTMLElement;
    const $viewMoreBtn = $viewMore.querySelector('#view-more-btn') as HTMLButtonElement;
    const $paging = $viewMore.querySelector('.quotes-paging') as HTMLElement;
    QueryString().delete('p');

    const pl: PageLoader<models.Quote> = new PageLoader<models.Quote>({
        clearResults: false,
        parent: $parent,
        nextBtn: $viewMoreBtn,
        beforeLoad: () => {
            if (pl.currentPage() === 0) {
                $parent.replaceChildren();
            }
            $viewMore.remove();
        },
        get: () => axios.get('/v2/quotes', {
            params: {
                'sort': pl.sort() || 'created desc',
                'max': pageMax,
                'data': quoteListData,
                'p': pl.currentPage(),
            },
        }).then(resp => {
            const data = models.QuotesHyejooResponse.fromJSON(resp.data);
            return { rows: data.quotes, nextPage: data.nextPage };
        }),
        draw: async quote => await DrawQuoteCard(quote),
        afterLoad: async (rows, nextPage) => {
            if (rows.length === 0 && pl.currentPage() === 0) {
                drawState($parent, 'empty');
                return;
            }
            const count = await total;
            const shown = Math.min((pl.currentPage() + 1) * pageMax, count);
            $paging.textContent = i18n('quotesPagingCount').replace('%s', shown.toLocaleString()).replace('%s', count.toLocaleString());
            $viewMoreBtn.classList.toggle('d-none', !nextPage);
            $parent.appendChild($viewMore);
        },
        onError: err => {
            ShowError(err);
            drawState($parent, 'error', () => pl.load());
        },
    });
    pl.load();
}
